import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { AdapterCatalogItem, AdapterFieldDefinition, AgentAdapterConfig, Agent, AgentExecutionConfig, AgentExecutionModeCatalogItem, AgentSharedConfig } from '@/types/api'

import {
  createAgent,
  createAgentAtomic,
  getAgentAdapterConfig,
  getAgentSharedConfig,
  getAgentExecutionConfig,
  killAgent,
  listAdapterFields,
  type AdapterFieldScope,
  listAdapters,
  listAgentExecutionModes,
  listAgents,
  listCompanySecrets,
  upsertCompanySecret,
  pauseAgent,
  putAgentExecutionSetup,
  resumeAgent,
  saveAgentAdapterConfig,
  saveAgentSharedConfig,
  updateAgent,
  getAgentInbox,
  type AgentExecutionSetupInput,
  type CompanySecret,
  type CreateAgentAtomicInput,
  type CreateAgentInput,
  type InboxEmail,
  type SaveAgentAdapterConfigInput,
  type SaveAgentSharedConfigInput,
  type UpdateAgentInput,
  type UpsertCompanySecretInput,
} from '@/lib/api/endpoints'
import { createOptimisticListMutation } from '@/lib/queries/_helpers'
import { queryKeys } from '@/lib/queries/keys'

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

interface UseAgentsOptions {
  /** Auto-refresh em milissegundos. `false` desabilita. Default: sem auto-refresh. */
  refetchInterval?: number | false
}

export function useAgents(
  companyId: string | null,
  opts: UseAgentsOptions = {},
) {
  return useQuery({
    queryKey: companyId
      ? queryKeys.agents.list(companyId)
      : queryKeys.agents.all,
    queryFn: () => listAgents(companyId!),
    enabled: !!companyId,
    refetchInterval: opts.refetchInterval ?? false,
  })
}

export function useAgentExecutionConfig(agentId: string | null) {
  return useQuery({
    queryKey: agentId ? queryKeys.agents.executionConfig(agentId) : queryKeys.agents.all,
    queryFn: () => getAgentExecutionConfig(agentId!),
    enabled: !!agentId,
  })
}

// Catálogo de modos (agent_execution_modes). Stale longo — mode novo é evento
// raro, cache de 5min evita refetch a cada navegação no AgentDetail.
export function useAgentExecutionModes() {
  return useQuery<AgentExecutionModeCatalogItem[]>({
    queryKey: queryKeys.agents.executionModes(),
    queryFn: () => listAgentExecutionModes(),
    staleTime: 5 * 60_000,
  })
}

export function useAdapters(companyId: string | null) {
  return useQuery<AdapterCatalogItem[]>({
    queryKey: companyId ? queryKeys.adapters.list(companyId) : queryKeys.adapters.all,
    queryFn: () => listAdapters(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  })
}

export function useAdapterFields(
  adapterId: string | null,
  scope?: AdapterFieldScope,
) {
  return useQuery<AdapterFieldDefinition[]>({
    queryKey: adapterId
      ? queryKeys.adapters.fields(adapterId, scope)
      : queryKeys.adapters.all,
    queryFn: () => listAdapterFields(adapterId!, scope),
    enabled: !!adapterId,
    staleTime: 5 * 60_000,
  })
}

export function useAgentAdapterConfig(agentId: string | null) {
  return useQuery<AgentAdapterConfig | null>({
    queryKey: agentId ? queryKeys.agents.adapterConfig(agentId) : queryKeys.agents.all,
    queryFn: () => getAgentAdapterConfig(agentId!),
    enabled: !!agentId,
  })
}

export function useSaveAgentAdapterConfig(agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveAgentAdapterConfigInput) => {
      if (!agentId) throw new Error('agentId obrigatório')
      return saveAgentAdapterConfig(agentId, input)
    },
    onSuccess: (cfg: AgentAdapterConfig) => {
      qc.setQueryData(queryKeys.agents.adapterConfig(cfg.agentId), cfg)
    },
  })
}

export function useAgentSharedConfig(agentId: string | null) {
  return useQuery<AgentSharedConfig>({
    queryKey: agentId ? queryKeys.agents.sharedConfig(agentId) : queryKeys.agents.all,
    queryFn: () => getAgentSharedConfig(agentId!),
    enabled: !!agentId,
  })
}

export function useSaveAgentSharedConfig(agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SaveAgentSharedConfigInput) => {
      if (!agentId) throw new Error('agentId obrigatório')
      return saveAgentSharedConfig(agentId, input)
    },
    onSuccess: (cfg: AgentSharedConfig) => {
      qc.setQueryData(queryKeys.agents.sharedConfig(cfg.agentId), cfg)
    },
  })
}

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------

const TEMP_PREFIX = 'tmp_agt_'

export function useCreateAgent(companyId: string | null) {
  const qc = useQueryClient()

  return useMutation(
    createOptimisticListMutation<Agent[], CreateAgentInput, Agent>({
      queryClient: qc,
      queryKey: companyId
        ? queryKeys.agents.list(companyId)
        : queryKeys.agents.all,
      mutationFn: (input) => {
        if (!companyId) throw new Error('companyId obrigatório')
        return createAgent(companyId, input)
      },
      applyOptimistic: (current, input) => {
        if (!companyId) return current
        const optimistic: Agent = {
          id: `${TEMP_PREFIX}${Date.now()}`,
          companyId,
          status: 'idle',
          currentBurnRate: 0,
          specialtyId: null,
          systemPrompt: null,
          requiresApproval: false,
          platformUrl: null,
          isSystem: false,
          createdAt: new Date().toISOString(),
          ...input,
        }
        return [...(current ?? []), optimistic]
      },
    }),
  )
}

export function useCreateAgentAtomic(companyId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateAgentAtomicInput) => {
      if (!companyId) throw new Error('companyId obrigatório')
      return createAgentAtomic(companyId, input)
    },
    onSuccess: () => {
      if (companyId) void qc.invalidateQueries({ queryKey: queryKeys.agents.list(companyId) })
    },
  })
}

interface UpdateAgentVars {
  agentId: string
  patch: UpdateAgentInput
}

export function useUpdateAgent(companyId: string | null) {
  const qc = useQueryClient()

  return useMutation(
    createOptimisticListMutation<Agent[], UpdateAgentVars, Agent>({
      queryClient: qc,
      queryKey: companyId
        ? queryKeys.agents.list(companyId)
        : queryKeys.agents.all,
      mutationFn: ({ agentId, patch }) => updateAgent(agentId, patch),
      applyOptimistic: (current, { agentId, patch }) =>
        (current ?? []).map((a) =>
          a.id === agentId ? { ...a, ...patch } : a,
        ),
    }),
  )
}

// -----------------------------------------------------------------------------
// Transitions (pause / resume / kill)
// -----------------------------------------------------------------------------

interface AgentTransitionConfig {
  fn: (id: string) => Promise<Agent>
  /** Status final esperado, usado pelo patch otimista. */
  optimisticStatus: Agent['status']
  /**
   * Quando true, zera `currentBurnRate` no patch otimista (pause / kill).
   * Mantém o burn na transição "resume" (sobe de novo no próximo heartbeat).
   */
  zeroBurn: boolean
  /**
   * Quando true, invalida também a lista de tasks da company. `kill` precisa
   * disso porque tarefas atribuídas ao agente morto ficam órfãs visualmente
   * — a tela de Tasks precisa refletir o "agente offline" no card.
   */
  invalidateTasks: boolean
}

function useAgentTransition(
  companyId: string | null,
  cfg: AgentTransitionConfig,
) {
  const qc = useQueryClient()
  const invalidateAlso =
    cfg.invalidateTasks && companyId
      ? [queryKeys.tasks.list(companyId)]
      : []

  return useMutation(
    createOptimisticListMutation<Agent[], string, Agent>({
      queryClient: qc,
      queryKey: companyId
        ? queryKeys.agents.list(companyId)
        : queryKeys.agents.all,
      invalidateAlso,
      mutationFn: cfg.fn,
      applyOptimistic: (current, agentId) =>
        (current ?? []).map((a) =>
          a.id === agentId
            ? {
                ...a,
                status: cfg.optimisticStatus,
                currentBurnRate: cfg.zeroBurn ? 0 : a.currentBurnRate,
              }
            : a,
        ),
    }),
  )
}

export function usePauseAgent(companyId: string | null) {
  return useAgentTransition(companyId, {
    fn: pauseAgent,
    optimisticStatus: 'paused',
    zeroBurn: true,
    invalidateTasks: false,
  })
}

export function useResumeAgent(companyId: string | null) {
  return useAgentTransition(companyId, {
    fn: resumeAgent,
    optimisticStatus: 'idle',
    zeroBurn: false,
    invalidateTasks: false,
  })
}

export function useKillAgent(companyId: string | null) {
  return useAgentTransition(companyId, {
    fn: killAgent,
    optimisticStatus: 'offline',
    zeroBurn: true,
    invalidateTasks: true,
  })
}

export function useAgentInbox(agentId: string | null) {
  return useQuery<InboxEmail[]>({
    queryKey: agentId ? queryKeys.agents.inbox(agentId) : queryKeys.agents.all,
    queryFn: () => getAgentInbox(agentId!),
    enabled: !!agentId,
    refetchInterval: 2 * 60_000,
  })
}

// -----------------------------------------------------------------------------
// Company Secrets (Vault)
// -----------------------------------------------------------------------------

export function useCompanySecrets(companyId: string | null) {
  return useQuery<CompanySecret[]>({
    queryKey: companyId
      ? queryKeys.companySecrets.list(companyId)
      : queryKeys.companySecrets.all,
    queryFn: () => listCompanySecrets(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

export function useUpsertCompanySecret(companyId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: UpsertCompanySecretInput) => {
      if (!companyId) throw new Error('companyId obrigatório')
      return upsertCompanySecret(companyId, input)
    },
    onSuccess: () => {
      if (companyId) {
        void qc.invalidateQueries({ queryKey: queryKeys.companySecrets.list(companyId) })
      }
    },
  })
}

export function usePutAgentExecutionSetup(agentId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AgentExecutionSetupInput) => {
      if (!agentId) throw new Error('agentId obrigatório')
      return putAgentExecutionSetup(agentId, input)
    },
    onSuccess: (cfg: AgentExecutionConfig) => {
      qc.setQueryData(queryKeys.agents.executionConfig(cfg.agentId), cfg)
    },
  })
}

